/** @file onionSkin: ghost instances of the frames around the playhead, drawn at 35 % over the grid. */
import { Constants } from '@babylonjs/core/Engines/constants.js';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import { createPuppetInstance } from '@zilo/pose3d-runtime-babylon/instance/createPuppetInstance';
import type { CameraSource, PuppetInstance } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';
import type { EditorState } from '../../state/editorTypes';
import { frameSeek } from './frameSeek';
import type { PreviewPuppet } from './previewPuppet';

/** Ghost opacity (plan: 35 % alpha). */
const ALPHA = 0.35;

/** `update` once per rendered frame after the main puppet; ghosts are off while playing, in Quads mode or with `camera.onion` 0. */
export type OnionSkin = { update(p: PreviewPuppet | null, s: EditorState, camera: CameraSource): void; dispose(): void };

/** One ghost: its frame offset, its instance and the meshes it added to the scene. */
type Ghost = { offset: number; instance: PuppetInstance; meshes: AbstractMesh[] };

/** A ghost on the shared bundle copy: no joint meshes, its sheet materials blended at a constant 35 %. */
async function ghost(scene: Scene, p: PreviewPuppet, offset: number): Promise<Ghost> {
  const before = new Set(scene.meshes);
  const instance = await createPuppetInstance(scene, p.bundle, { meshes: false, blendSeconds: 0 });
  const meshes = scene.meshes.filter((m) => !before.has(m));
  for (const m of meshes) {
    const material = m.material;
    if (material === null) continue;
    material.alpha = ALPHA;
    material.alphaMode = Constants.ALPHA_INTERPOLATE;
    material.disableDepthWrite = true;
  }
  return { offset, instance, meshes };
}

/** Seeks and draws one ghost at `frame` of `clip`, or hides it (no clip, or a frame outside the clip). */
function showGhost(g: Ghost, clip: Clip | undefined, frame: number, camera: CameraSource): void {
  const on = clip !== undefined && frame >= 0 && frame <= clip.frames;
  for (const m of g.meshes) m.setEnabled(on);
  if (!on) return;
  frameSeek(g.instance, clip.name, frame);
  g.instance.update(0, camera);
}

/**
 * Frames `playhead.frame +- 1..n` (`n` = `camera.onion`) of the playhead clip, each a second instance on the same
 * bundle copy (so a drag's override shows in the ghosts too) seeked with `frameSeek`; the engine's blend constants
 * make the opaque quad shader draw at 35 % (`ALPHA_INTERPOLATE`). Rebuilt when the puppet or `n` changes.
 */
export function onionSkin(scene: Scene): OnionSkin {
  let ghosts: Ghost[] = [];
  let built: { p: PreviewPuppet | null; n: number } = { p: null, n: 0 };
  const clear = (): void => { for (const g of ghosts) g.instance.dispose(); ghosts = []; };
  const rebuild = (p: PreviewPuppet | null, n: number): void => {
    clear();
    built = { p, n };
    if (p === null || n === 0) return;
    const offsets = Array.from({ length: n }, (_, i) => [-(i + 1), i + 1]).flat();
    void Promise.all(offsets.map((o) => ghost(scene, p, o))).then((made) => {
      if (built.p !== p || built.n !== n || scene.isDisposed) { for (const g of made) g.instance.dispose(); return; }
      ghosts = made;
    });
  };
  return {
    update: (p, s, camera) => {
      const n = Math.max(0, Math.min(3, Math.round(s.camera.onion)));
      if (p !== built.p || n !== built.n) rebuild(p, n);
      const clip = s.playhead.clip === null ? undefined : p?.clip(s.playhead.clip);
      scene.getEngine().setAlphaConstants(ALPHA, ALPHA, ALPHA, ALPHA);
      for (const g of ghosts) showGhost(g, s.playhead.playing || s.camera.quads ? undefined : clip, s.playhead.frame + g.offset, camera);
    },
    dispose: clear,
  };
}
